<template>
  <v-card outlined>
    <v-card-title class="pb-0">
      <v-text-field
        v-model="defaultTitle"
        :label="`Default Title (${defaultLocale.toUpperCase()})`"
        required
        outlined
        dense
      ></v-text-field>
    </v-card-title>

    <v-divider class="my-4"></v-divider>

    <v-card-text>
      <v-simple-table>
        <thead>
          <tr>
            <th class="text-left">{{ $t('language') }}</th>
            <th class="text-left">{{ $t('translatedTitle') }}</th>
            <th class="text-left">{{ $t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="lang in otherLanguages"
            :key="lang.code"
          >
            <td>{{ lang.name }}</td>
            <td>{{ getTitle(lang.code) || '—' }}</td>
            <td>
              <v-btn
                small
                color="primary"
                @click="openDialog(lang)"
              >
                <v-icon left small>
                  {{ getTitle(lang.code) ? 'mdi-pencil' : 'mdi-plus' }}
                </v-icon>
                {{ getTitle(lang.code) ? 'Edit' : 'Add' }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>

    <v-dialog v-model="dialog.visible" max-width="500px">
      <v-card>
        <v-card-title class="headline">
          {{ dialog.mode === 'edit' ? 'Edit Translation' : 'Add Translation' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dialog.tempTitle"
            label="Title"
            outlined
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey"
            :lang="$i18n.locale"
            @click="dialog.visible = false"
          >
            {{ $t('cancel') }}
          </v-btn>

          <v-btn
            color="primary"
            :lang="$i18n.locale"
            @click="saveTranslation"
          >
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import { SUPPORTED_LANGUAGES } from '@/i18n/config';
  import { required } from "vuelidate/lib/validators";
  import { mapState, mapActions } from 'vuex';

  export default {
    name: "MuseumTitleTranslations",
    props: {
      supportedLanguages: {
        type: Array,
        default: () => SUPPORTED_LANGUAGES
      },
      defaultLocale: {
        type: String,
        default: "el"
      },
      editMode: {
        type: Boolean,
        default: false // false for NewMuseum, true for EditMuseum
      }
    },
    data() {
      return {
        dialog: {
          visible: false,
          lang: null,
          tempTitle: "",
          mode: "add"
        }
      };
    },
    computed: {
      ...mapState({
        activeMuseum: (state) => state.superuser.activeMuseum,
        editMuseumObj: (state) => state.superuser.editMuseumObj
      }),
      targetMuseum() {
        let result = {};
        if (this.editMode) {
          result = this.editMuseumObj;
        } else {
          result = this.$store.state.superuser.newMuseumObj;
        }
        return result;
      },
      defaultTitle: {
        get() {
          return this.targetMuseum?.defaultTitle || "";
        },
        set(value) {
          this.setMuseumProp({ prop: 'defaultTitle', value: value });
        }
      },
      locales: {
        get() {
          return this.targetMuseum?.museumLocales || [];
        },
        set(value) {
          this.setMuseumProp({ prop: 'museumLocales', value: value });
        }
      },
      otherLanguages() {
        return this.supportedLanguages.filter(lang => lang.code !== this.defaultLocale);
      }
    },
    mounted() {
      if (this.editMode) {
        let activeMuseumCopy = JSON.parse(JSON.stringify(this.activeMuseum));
        this.setEditMuseumObject(activeMuseumCopy);
      }
    },
    methods: {
      ...mapActions(['setNewMuseumObjectProp', 'setEditMuseumObjectProp', 'setEditMuseumObject']),
      setMuseumProp(payload) {
        if (this.editMode) {
          this.setEditMuseumObjectProp(payload);
        } else {
          this.setNewMuseumObjectProp(payload);
        }
      },
      getTitle(locale) {
        const found = this.locales.find(l => l.locale === locale);
        return found ? found.title : "";
      },
      openDialog(lang) {
        const existing = this.getTitle(lang.code);
        this.dialog = {
          visible: true,
          lang,
          tempTitle: existing,
          mode: existing ? "edit" : "add"
        };
      },
      saveTranslation() {
        const { lang, tempTitle } = this.dialog;
        const locales = [...this.locales];
        const index = locales.findIndex(l => l.locale === lang.code);

        if (index !== -1) {
          locales[index].title = tempTitle;
        } else {
          locales.push({ locale: lang.code, title: tempTitle });
        }
        this.locales = locales;
        this.dialog.visible = false;
      }
    },
    watch: {
      museum: {
        immediate: true,
        handler(newVal) {
          if (this.editMode && newVal && newVal.locales) {
            this.localCopy = [...newVal.locales];
          }
        }
      }
    }
  };
</script>
